import type { ButtonProps } from '@talos/kyoko';
import { Button, ButtonVariants, setAlpha, Tooltip } from '@talos/kyoko';
import type { ReactNode } from 'react';
import styled from 'styled-components';

interface HeaderButtonProps extends ButtonProps {
  borderPlacement: HeaderButtonBorder;
  compact?: boolean;
  children?: ReactNode;
  tooltip?: ReactNode;
}

export const HeaderButton = ({ compact = false, children, tooltip, ...props }: HeaderButtonProps) => {
  const tooltipContent = tooltip ?? children;
  const button = (
    <StyledHeaderButton {...props} compact={compact}>
      {compact && !tooltip ? undefined : children}
    </StyledHeaderButton>
  );

  if ((!tooltip && !compact) || tooltipContent == null) {
    return button;
  }

  return (
    <Tooltip tooltip={tooltipContent} placement="bottom" targetStyle={{ height: '100%' }}>
      {button}
    </Tooltip>
  );
};

export type HeaderButtonBorder = 'left' | 'right' | 'both';

const StyledHeaderButton = styled(Button)<{ borderPlacement: HeaderButtonBorder; compact?: boolean }>`
  && {
    color: ${({ variant }) =>
      variant === ButtonVariants.Priority
        ? 'var(--colors-gray-100)'
        : variant === ButtonVariants.Negative
          ? ''
          : 'var(--colors-gray-090)'};
    border-radius: 0;
    border: none;
    padding: 0 ${({ theme, compact }) => (compact ? theme.spacing8 : theme.spacing12)}px;
    height: 100%;
    background: ${({ variant, theme }) =>
      variant === ButtonVariants.Priority
        ? setAlpha(0.06, theme.colors.gray['100'])
        : variant === ButtonVariants.Default
          ? 'transparent'
          : ''};
    &,
    &:hover,
    &:focus {
      ${({ borderPlacement: border }) =>
        border === 'left' || border === 'both' ? `border-left: 1px solid var(--backgroundBody);` : ''}
      ${({ borderPlacement: border }) =>
        border === 'right' || border === 'both' ? `border-right: 1px solid var(--backgroundBody);` : ''}
    }
    &:hover,
    &:focus {
      background: ${({ variant, theme }) =>
        variant === ButtonVariants.Priority
          ? setAlpha(0.08, theme.colors.gray['100'])
          : variant === ButtonVariants.Default
            ? setAlpha(0.04, theme.colors.gray['100'])
            : ''};
      color: ${({ variant }) => (variant === ButtonVariants.Priority ? 'var(--colors-gray-100)' : '')};
    }
  }
`;
